import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Briefcase,
  CheckCircle,
  XCircle,
  DollarSign,
  Tag,
  Clock,
  TrendingUp,
} from "lucide-react";
import { BusinessService } from "../../types/businessService";
import { getBusinessServices, Tax, getCreateFormData } from "../../services/businessServicesApi";
import PageHeader from "../../components/ui/PageHeader";
import GlobalLoader from "../../components/ui/GlobalLoader";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from "recharts";

export default function ServicesSummaryPage() {
  const [loading, setLoading] = useState(true);
  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [services, setServices] = useState<BusinessService[]>([]);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const [servicesData, formData] = await Promise.all([
        getBusinessServices(),
        getCreateFormData()
      ]);
      setServices(servicesData || []);
      setTaxes(formData.taxes || []);
    } catch (error) {
      console.error("Failed to fetch services or taxes", error);
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(() => {
    const active = services.filter((s) => s.status === "active").length;
    const inactive = services.filter((s) => s.status === "inactive").length;
    const avgPrice =
      services.length > 0
        ? (
          services.reduce((sum, s) => sum + (s.price || 0), 0) /
          services.length
        ).toFixed(2)
        : "0.00";
    const maxPrice =
      services.length > 0 ? Math.max(...services.map((s) => s.price || 0)) : 0;

    return { active, inactive, avgPrice, maxPrice };
  }, [services]);

  const statusData = useMemo(
    () => [
      { name: "Active", value: stats.active, color: "#885ABB" },
      { name: "Inactive", value: stats.inactive, color: "#E9D5FF" },
    ],
    [stats]
  );

  const priceDistributionData = useMemo(() => {
    const ranges = [
      { name: "$0-20", min: 0, max: 20, count: 0 },
      { name: "$21-50", min: 21, max: 50, count: 0 },
      { name: "$51-100", min: 51, max: 100, count: 0 },
      { name: "$100+", min: 101, max: Infinity, count: 0 },
    ];

    services.forEach((s) => {
      const price = s.price || 0;
      const range = ranges.find((r) => price >= r.min && price <= r.max);
      if (range) range.count++;
    });

    return ranges;
  }, [services]);

  const topServices = useMemo(() => {
    return [...services]
      .sort((a, b) => (b.price || 0) - (a.price || 0))
      .slice(0, 5);
  }, [services]);

  if (loading) {
    return (
      <div className="flex h-[80vh] items-center justify-center">
        <GlobalLoader description="Loading services summary..." />
      </div>
    );
  }

  return (
    <div className="w-full">
      <PageHeader
        title="Services Summary"
        description="Detailed analytics and overview of your service offerings"
        icon={Briefcase}
        variant="default"
        className="mb-0"
      />

      <div className="space-y-6 pt-4">
        {/* Metric Cards - Compact Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            label="Total Services"
            value={services.length}
            icon={Briefcase}
            trend="+2 this month" // Mock trend
            trendUp={true}
          />
          <MetricCard
            label="Active Services"
            value={stats.active}
            icon={CheckCircle}
            subValue={`${Math.round(
              (stats.active / (services.length || 1)) * 100
            )}% of total`}
          />
          <MetricCard
            label="Average Price"
            value={`$${stats.avgPrice}`}
            icon={DollarSign}
          />
          <MetricCard
            label="Highest Price"
            value={`$${stats.maxPrice}`}
            icon={TrendingUp}
          />
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Price Distribution Bar Chart */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h3 className="text-lg font-semibold text-[#402B69] mb-6">
              Price Distribution
            </h3>
            <div className="h-[250px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={priceDistributionData}
                  margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#F3F4F6"
                  />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#6B7280", fontSize: 12 }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#6B7280", fontSize: 12 }}
                  />
                  <RechartsTooltip
                    cursor={{ fill: "#F9FAFB" }}
                    contentStyle={{
                      borderRadius: "8px",
                      border: "none",
                      boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                    }}
                  />
                  <Bar
                    dataKey="count"
                    fill="#885ABB"
                    radius={[4, 4, 0, 0]}
                    barSize={40}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Status Donut Chart */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h3 className="text-lg font-semibold text-[#402B69] mb-4">
              Service Status
            </h3>
            <div className="h-[200px] w-full relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={0}
                    dataKey="value"
                    stroke="none"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
              {/* Center Text */}
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-2xl font-bold text-gray-900">
                  {stats.active}
                </span>
                <span className="text-xs text-gray-500 font-medium">
                  Active
                </span>
              </div>
            </div>
            <div className="flex items-center justify-center gap-6 mt-4">
              {statusData.map((item) => (
                <div key={item.name} className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-sm text-gray-600">
                    {item.name} ({item.value})
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Detailed Table Section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-[#402B69]">
              Premium Services
            </h3>
            <Link
              to="/manage/services"
              className="text-sm text-[#885ABB] font-medium hover:text-[#6f47a2] transition-colors"
            >
              View All
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-[#885ABB] text-white font-medium">
                <tr>
                  <th className="px-6 py-3 font-semibold">Service Name</th>
                  <th className="px-6 py-3 font-semibold">Duration</th>
                  <th className="px-6 py-3 font-semibold">Category</th>
                  <th className="px-6 py-3 font-semibold">Tax</th>
                  <th className="px-6 py-3 font-semibold text-right">Price</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {topServices.map((service) => (
                  <tr
                    key={service.id}
                    className="group hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">
                        {service.name}
                      </div>
                      <div className="text-xs text-gray-500 truncate max-w-[200px] mt-0.5">
                        {service.description || "No description"}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-500">
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 text-gray-400" />
                        {service.duration} {service.timeUnit || "min"}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-500">
                      {service.categoryName || (
                        <span className="italic text-gray-400">
                          Uncategorized
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-500">
                      {service.taxes && service.taxes.length > 0 ? (
                        <div className="flex flex-col gap-1">
                          {service.taxes.map((taxId, i) => {
                            const tax = taxes.find((t) => t.id === Number(taxId));
                            if (!tax) return null;
                            const amount = (service.price * tax.percent) / 100;
                            return (
                              <div key={i} className="text-xs">
                                <span className="font-medium text-gray-700">{tax.name}:</span>{" "}
                                <span className="text-[#885ABB]">
                                  ${amount.toFixed(2)} ({tax.percent}%)
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">No tax</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right font-semibold text-gray-900">
                      ${service.price}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({
  label,
  value,
  icon: Icon,
  subValue,
  trend,
  trendUp,
}: any) {
  return (
    <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-between h-full">
      <div className="flex items-start justify-between mb-4">
        <div className="p-2.5 bg-purple-50 rounded-lg">
          <Icon className="w-5 h-5 text-[#885ABB]" />
        </div>
        {trend && (
          <span
            className={`text-xs font-medium px-2 py-1 rounded-full ${trendUp ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"
              }`}
          >
            {trend}
          </span>
        )}
      </div>
      <div>
        <h3 className="text-sm font-medium text-gray-500 mb-1">{label}</h3>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold text-gray-900">{value}</span>
          {subValue && (
            <span className="text-xs text-gray-400">{subValue}</span>
          )}
        </div>
      </div>
    </div>
  );
}
